/* === src/pages/public/BursaryDetailsPage.js (Hub & Spoke Enabled) === */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Container, Typography, Button, Paper, Box, Chip, Divider, Alert, Grid, 
    List, ListItem, ListItemIcon, ListItemText, Stack, Card, CardContent 
} from '@mui/material';
import { 
    CheckCircle as CheckIcon, 
    InsertDriveFile as DocIcon, 
    CalendarToday as DateIcon, 
    MonetizationOn as MoneyIcon, 
    Business as BusinessIcon,
    ArrowBack as BackIcon,
    FactCheck as RubricIcon,
    OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import api from '../../../services/api';
import { isProfileIncomplete } from '../../../services/profileGuard';
import { useAuth } from '../../../context/AuthContext';
import { FormSkeleton } from '../../../components/Skeletons'; 
import { tenantConfig } from '../../../config/tenantConfig'; 

const BursaryDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, token } = useAuth();
    
    const [bursary, setBursary] = useState(null);
    const [rubric, setRubric] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const [requirements, setRequirements] = useState([]);
    const [documents, setDocuments] = useState([]);
    
    const [applicationStatus, setApplicationStatus] = useState(null);

    const platformDescription = "BrightSparc is South Africa's secure, cloud-native platform connecting ambitious students with dedicated funders. Say goodbye to manual spreadsheets and fragmented applications.";

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                // 🚨 MULTI-TENANT FIX: Pass the tenant ID to the API so it queries the right DB
                const [res, rubricRes] = await Promise.all([
                    api.get(`/public/bursaries/${id}?tenant=${tenantConfig.id}`),
                    api.get(`/public/bursaries/${id}/rubric?tenant=${tenantConfig.id}`).catch(() => ({ data: [] }))
                ]);
                
                setBursary(res.data);
                setRubric(rubricRes.data || []);
                
                let raw = res.data.EligibilityCriteria;
                let reqs = [];
                let docs = [];

                if (raw) {
                    try {
                        let parsed = null;
                        if (typeof raw === 'object') parsed = raw;
                        else if (typeof raw === 'string') {
                            if (raw.startsWith('"') && raw.endsWith('"')) raw = JSON.parse(raw);
                            parsed = JSON.parse(raw);
                        }

                        if (parsed) {
                            if (Array.isArray(parsed)) reqs = parsed;
                            else {
                                if (Array.isArray(parsed.requirements)) reqs = parsed.requirements;
                                if (Array.isArray(parsed.documents)) docs = parsed.documents;
                            }
                        }
                    } catch (e) {
                        reqs = [String(raw)];
                    }
                }
                
                setRequirements(reqs);
                setDocuments(docs);

                const currentRole = user?.role || user?.Role;
                if (token && currentRole === 'Applicant') {
                    try {
                        const appRes = await api.get(`/applicant/application?fundId=${id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        if (appRes.data) {
                            setApplicationStatus(appRes.data.Status);
                        }
                    } catch (appErr) {
                        console.warn("Could not fetch application status:", appErr);
                    }
                }

            } catch (err) {
                console.error("Fetch error:", err);
                setError("Unable to load bursary details. It may have been removed or you are on the wrong portal.");
            } finally {
                setLoading(false);
            }
        };
        fetchDetails();
    }, [id, user, token]);

    // 🚨 THE FIX: Make the function async to await the profile check
    const handleApply = async () => {
        // CROSS-DOMAIN LOGIC: If on Central Hub but bursary belongs to a Tenant
        const isExternalTenant = tenantConfig.id === 'BRIGHTSPARC' && !!bursary.TenantDomain;
        
        if (isExternalTenant) {
            const baseUrl = bursary.TenantDomain.replace(/\/$/, '');
            window.location.href = `${baseUrl}/auth?redirect=/applicant/form?fundId=${bursary.Id}`;
            return;
        }

        const url = `/applicant/form?fundId=${id}`;
        
        if (user) {
            // 🚨 THE FIX: Implement Profile Guard logic mirroring HomePage
            try {
                const res = await api.get('/applicant/profile', { 
                    headers: { Authorization: `Bearer ${token}` } 
                });
                
                const profileData = res.data;

                if (!profileData || isProfileIncomplete(profileData)) {
                    navigate('/profile?reason=incomplete_profile');
                    return;
                }
            } catch (err) {
                console.error("Interceptor safety fallback:", err);
            }
            
            navigate(url);
        } else {
            navigate(`/auth?redirect=${url}`);
        }
    };

    if (loading) return <Container maxWidth="md" sx={{ mt: 4 }}><FormSkeleton /></Container>;

    if (error) return (
        <Container maxWidth="md" sx={{ mt: 8 }}>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Button startIcon={<BackIcon />} onClick={() => navigate('/')}>Back to Home</Button>
        </Container>
    );

    if (!bursary) return null;

    // Safely check if the date exists before comparing
    const isClosed = bursary.ApplicationEndDate 
        ? new Date(bursary.ApplicationEndDate) < new Date() 
        : false;
    const userRole = user?.role || user?.Role;
    
    const isExternalTenant = tenantConfig.id === 'BRIGHTSPARC' && !!bursary.TenantDomain;

    let btnText = isExternalTenant ? "Apply on Tenant Portal" : "Apply Now";
    let btnColor = "primary";
    let btnVariant = "contained";
    let isDisabled = false;

    if (userRole && userRole !== 'Applicant') {
        btnText = "Applicants Only";
        isDisabled = true;
        btnVariant = "outlined";
        btnColor = "secondary";
    } 
    else if (isClosed) {
        btnText = "Applications Closed";
        isDisabled = true;
        btnVariant = "outlined";
        btnColor = "error";
    } 
    else if (applicationStatus) {
        if (applicationStatus === 'Draft') {
            btnText = "Continue Draft";
            btnColor = "warning";
        } else if (applicationStatus === 'Withdrawn') {
            btnText = "Apply Again (Withdrawn)";
            btnColor = "primary";
        } else {
            btnText = `Status: ${applicationStatus}`; 
            isDisabled = true; 
            btnColor = "success";
        }
    }

    return (
        <>
            <Helmet>
                <title>{bursary.FundName ? `${bursary.FundName} | BrightSparc` : 'Bursary Details | BrightSparc'}</title>
                <meta name="description" content={platformDescription} />
            </Helmet>

            <Container maxWidth="lg" sx={{ py: 6 }}>
                <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} sx={{ mb: 3 }}>Back</Button>

                <Grid container spacing={4}>
                    <Grid item xs={12} md={8}>
                        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Chip label={bursary.FundCategory || 'General'} color="primary" size="small" sx={{ mb: 1 }} />
                                    {isClosed && <Chip label="Applications Closed" color="error" size="small" />}
                                </Box>
                                
                                <Typography variant="h3" fontWeight="800" gutterBottom sx={{ lineHeight: 1.2 }}>
                                    {bursary.FundName}
                                </Typography>
                                
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.secondary', mt: 1 }}>
                                    <BusinessIcon fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight="500">Provided by {bursary.Sponsor}</Typography>
                                </Stack>
                            </Box>

                            <Divider sx={{ my: 4 }} />

                            <Box sx={{ mb: 5, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.6 }}>
                                    {platformDescription}
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 5 }}>
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    About the Bursary
                                </Typography>
                                <Typography 
                                    component="div"
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ 
                                        lineHeight: 1.8, 
                                        textAlign: 'justify',
                                        '& p': { mb: 2, mt: 0, fontFamily: 'inherit' },
                                        '& ul, & ol': { mb: 2, pl: 3, fontFamily: 'inherit' },
                                        '& li': { fontFamily: 'inherit' },
                                        '& a': { color: 'primary.main', textDecoration: 'underline', fontFamily: 'inherit' },
                                        '& strong': { fontWeight: 'bold' },
                                        '& em': { fontStyle: 'italic' }
                                    }}
                                    // 🚨 THE FIX: Fallback string prevents a fatal React crash
                                    dangerouslySetInnerHTML={{ __html: bursary.Description || 'No description provided for this fund.' }}
                                />
                            </Box>

                            <Box sx={{ mb: 5 }}>
                                <Typography variant="h5" fontWeight="bold" gutterBottom>Eligibility Criteria</Typography>
                                {requirements.length > 0 ? (
                                    <List dense>
                                        {requirements.map((req, index) => (
                                            <ListItem key={index} alignItems="flex-start" sx={{ pl: 0 }}>
                                                <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                                                <ListItemText primary={req} primaryTypographyProps={{ variant: 'body1', sx: { lineHeight: 1.6 } }} />
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>No specific criteria listed. Please check the description.</Typography>
                                )}
                            </Box>

                            {/* Rubric Criteria Display */}
                            {rubric.length > 0 && (
                                <Box sx={{ mb: 5 }}>
                                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <RubricIcon color="primary" /> Evaluation Criteria
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        This application will be scored based on the following rubric framework:
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {rubric.map((item, index) => (
                                            <Grid item xs={12} sm={6} key={index}>
                                                <Paper 
                                                    elevation={0} 
                                                    sx={{ 
                                                        p: 2, 
                                                        bgcolor: 'background.default', 
                                                        border: '1px solid', 
                                                        borderColor: 'divider',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <Typography variant="body1" fontWeight="500">{item.CriteriaName}</Typography>
                                                    <Chip label={`${item.MaxScore} Pts`} size="small" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}

                            {documents.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>Required Documents</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Please ensure you have the following ready before applying:</Typography>
                                    <Grid container spacing={2}>
                                        {documents.map((doc, index) => (
                                            <Grid item xs={12} sm={6} key={index}>
                                                <Paper 
                                                    elevation={0} 
                                                    sx={{ 
                                                        p: 2, 
                                                        bgcolor: 'action.hover', 
                                                        border: '1px solid', 
                                                        borderColor: 'divider', 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        gap: 2 
                                                    }}
                                                >
                                                    <DocIcon color="action" />
                                                    <Typography variant="body2" fontWeight="500">{doc}</Typography>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* --- SIDEBAR (RIGHT) --- */}
                    <Grid item xs={12} md={4}>
                        <Card elevation={4} sx={{ borderRadius: 3, position: 'sticky', top: 20 }}>
                            <CardContent sx={{ p: 3 }}>
                                
                                {tenantConfig.features.showFundAmount !== false && (
                                    <>
                                        <Typography variant="overline" color="text.secondary" fontWeight="bold">Value</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                            <MoneyIcon color="primary" fontSize="large" />
                                            <Typography variant="h4" fontWeight="bold">
                                                R {Number(bursary.TotalAmount || bursary.Amount).toLocaleString()}
                                            </Typography>
                                        </Box>
                                        <Divider sx={{ mb: 3 }} />
                                    </>
                                )}

                                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 2, fontStyle: 'italic' }}>
                                    Say goodbye to manual spreadsheets and fragmented applications.
                                </Typography>

                                <Typography variant="overline" color="text.secondary" fontWeight="bold">Closing Date</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <DateIcon color="action" />
                                    <Typography variant="h6">
                                        {/* 🚨 THE FIX: Safely handle missing dates */}
                                        {bursary.ApplicationEndDate ? new Date(bursary.ApplicationEndDate).toLocaleDateString(undefined, {
                                            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                                        }) : 'Open until further notice'}
                                    </Typography>
                                </Box>

                                <Button 
                                    variant={btnVariant}
                                    color={btnColor}
                                    size="large" 
                                    fullWidth 
                                    onClick={handleApply}
                                    disabled={isDisabled}
                                    sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
                                    endIcon={isExternalTenant && !isDisabled ? <OpenInNewIcon /> : null}
                                >
                                    {btnText}
                                </Button>
                                
                                {(!isClosed && !user) && (
                                    <Typography variant="caption" display="block" align="center" sx={{ mt: 2, color: 'text.secondary' }}>
                                        Login required to apply.
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </>
    );
};

export default BursaryDetailsPage;